import {sanitizeRichTextHtml} from "./richText";

const BLOCK_SELECTOR = "div, p";

function isHighlight(element: Element | null): element is HTMLSpanElement {
  return element instanceof HTMLSpanElement && element.classList.contains("highlight");
}

function closestHighlight(node: Node, root: HTMLElement): HTMLSpanElement | null {
  const element = node instanceof Element ? node : node.parentElement;
  const highlight = element?.closest("span.highlight") ?? null;
  return highlight && root.contains(highlight) ? (highlight as HTMLSpanElement) : null;
}

function isBreak(node: Node | null): node is HTMLBRElement {
  return node instanceof HTMLBRElement;
}

function previousContentNode(node: Node): Node | null {
  let current = node.previousSibling;
  while (current?.nodeType === Node.TEXT_NODE && current.textContent === "") {
    current = current.previousSibling;
  }
  return current;
}

function nextContentNode(node: Node): Node | null {
  let current = node.nextSibling;
  while (current?.nodeType === Node.TEXT_NODE && current.textContent === "") {
    current = current.nextSibling;
  }
  return current;
}

function hasRangeContent(range: Range): boolean {
  if (range.toString().length > 0) return true;
  const fragment = range.cloneContents();
  return Boolean(fragment.querySelector("br, img, svg"));
}

function hasContentBeforeCaret(highlight: HTMLSpanElement, caret: Range): boolean {
  const range = document.createRange();
  range.selectNodeContents(highlight);
  range.setEnd(caret.startContainer, caret.startOffset);
  return hasRangeContent(range);
}

function hasContentAfterCaret(highlight: HTMLSpanElement, caret: Range): boolean {
  const range = document.createRange();
  range.selectNodeContents(highlight);
  range.setStart(caret.startContainer, caret.startOffset);
  return hasRangeContent(range);
}

function hasSameFormat(left: HTMLSpanElement, right: HTMLSpanElement): boolean {
  return (
    left.className === right.className && left.getAttribute("style") === right.getAttribute("style")
  );
}

function restoreCaret(selection: Selection, container: Node, offset: number): void {
  const range = document.createRange();
  range.setStart(container, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Repair legacy rich-text markup before it enters a contenteditable element.
 * Old video placeholders ended with an opening span tag. Browsers parse that
 * tag as a nested empty span and move line breaks into it during edits.
 */
export function normalizeContentEditableHtml(html: string, colorCascade = false): string {
  if (!html || typeof document === "undefined" || !html.includes("<")) {
    return html;
  }

  html = sanitizeRichTextHtml(html, document.defaultView!);

  const template = document.createElement("template");
  template.innerHTML = html;

  // Expose native inline colors to the same cascade as field colors.
  for (const element of colorCascade ? template.content.querySelectorAll<HTMLElement>("[style]") : []) {
    if (element.style.color && !element.style.color.includes("var(")) {
      element.style.setProperty("--coverbaker-fragment-color", element.style.color);
    }
  }

  for (const span of Array.from(template.content.querySelectorAll("span"))) {
    if (span.attributes.length !== 0) continue;
    span.replaceWith(...Array.from(span.childNodes));
  }

  for (const block of Array.from(template.content.querySelectorAll(BLOCK_SELECTOR))) {
    const replacement = document.createDocumentFragment();
    if (previousContentNode(block) && !isBreak(previousContentNode(block))) {
      replacement.append(document.createElement("br"));
    }
    replacement.append(...Array.from(block.childNodes));
    if (nextContentNode(block) && !isBreak(nextContentNode(block))) {
      replacement.append(document.createElement("br"));
    }
    block.replaceWith(replacement);
  }

  for (const highlight of Array.from(
    template.content.querySelectorAll<HTMLSpanElement>("span.highlight")
  )) {
    if (!Array.from(highlight.childNodes).some(isBreak)) continue;

    const replacement = document.createDocumentFragment();
    let segment = highlight.cloneNode(false) as HTMLSpanElement;
    for (const child of Array.from(highlight.childNodes)) {
      if (isBreak(child)) {
        replacement.append(segment, child);
        segment = highlight.cloneNode(false) as HTMLSpanElement;
      } else {
        segment.append(child);
      }
    }
    replacement.append(segment);
    highlight.replaceWith(replacement);
  }

  return template.innerHTML;
}

/** Insert one flat line break and preserve the current highlight style. */
export function insertContentEditableLineBreak(root: HTMLElement, selection: Selection, plain = false): boolean {
  if (selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;

  range.deleteContents();
  range.collapse(true);

  // Former textareas use white-space: pre-wrap. Keep their line breaks in
  // text nodes so a trailing HTML break cannot swallow the next keystroke.
  if (plain) {
    const newline = root.ownerDocument.createTextNode("\n");
    range.insertNode(newline);
    restoreCaret(selection, newline, 1);
    root.focus();
    return true;
  }

  const marker = document.createElement("span");
  marker.dataset.contentEditableBreak = "";
  range.insertNode(marker);

  const highlight = closestHighlight(marker, root);
  const lineBreak = document.createElement("br");

  if (highlight) {
    const trailingRange = document.createRange();
    trailingRange.setStartAfter(marker);
    trailingRange.setEnd(highlight, highlight.childNodes.length);
    const trailingContent = trailingRange.extractContents();
    marker.remove();

    const nextHighlight = highlight.cloneNode(false) as HTMLSpanElement;
    nextHighlight.append(trailingContent);
    highlight.after(lineBreak, nextHighlight);
    restoreCaret(selection, nextHighlight, 0);
  } else {
    const parent = marker.parentNode;
    if (!parent) return false;
    const markerIndex = Array.prototype.indexOf.call(parent.childNodes, marker);
    marker.replaceWith(lineBreak);
    restoreCaret(selection, parent, markerIndex + 1);
  }

  root.focus();
  return true;
}

/** Merge two matching highlighted lines when Backspace or Delete removes their break. */
export function mergeContentEditableHighlightLines(
  root: HTMLElement,
  selection: Selection,
  direction: "backward" | "forward"
): boolean {
  if (selection.rangeCount === 0) return false;
  const caret = selection.getRangeAt(0);
  if (!caret.collapsed || !root.contains(caret.startContainer)) return false;

  if (caret.startContainer === root) {
    const breakIndex = direction === "backward" ? caret.startOffset - 1 : caret.startOffset;
    const lineBreak = root.childNodes[breakIndex] ?? null;
    if (isBreak(lineBreak)) {
      lineBreak.remove();
      restoreCaret(selection, root, breakIndex);
      return true;
    }
  }

  const current = closestHighlight(caret.startContainer, root);
  if (!current) return false;

  if (direction === "backward") {
    if (hasContentBeforeCaret(current, caret)) return false;
    const lineBreak = previousContentNode(current);
    const previous = lineBreak ? previousContentNode(lineBreak) : null;
    if (!isBreak(lineBreak) || !(previous instanceof HTMLSpanElement)) return false;
    if (!isHighlight(previous) || !hasSameFormat(previous, current)) return false;

    const caretOffset = previous.childNodes.length;
    previous.append(...Array.from(current.childNodes));
    lineBreak.remove();
    current.remove();
    restoreCaret(selection, previous, caretOffset);
    return true;
  }

  if (hasContentAfterCaret(current, caret)) return false;
  const lineBreak = nextContentNode(current);
  const next = lineBreak ? nextContentNode(lineBreak) : null;
  if (!isBreak(lineBreak) || !(next instanceof HTMLSpanElement)) return false;
  if (!isHighlight(next) || !hasSameFormat(current, next)) return false;

  const caretOffset = current.childNodes.length;
  current.append(...Array.from(next.childNodes));
  lineBreak.remove();
  next.remove();
  restoreCaret(selection, current, caretOffset);
  return true;
}
